import * as Api from '@ViewModels';
import { ESelectionState } from '@ViewModels';
import { StyleDeclarationValue } from 'aphrodite';
import equal from 'fast-deep-equal';
import produce from 'immer';
import { inject, observer } from 'mobx-react';
import { parse as getQueryStringParams } from '@AppModels/QueryStringUtils';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ContactSortKey, ILocationState } from '../../../../models';
import { IQuickAddEntityComponentProps, QuickAddEntityViewModelKey } from '../../../../models/AppState';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { PersistentStorageManager } from '../../../../models/Storage';
import { queryStringParamsToSearchRequest } from '../../../../models/UiUtils';
import { invalidateAllContactFilterQueries, useContactFilterQuery } from '../../../../queries';
import {
	AdvancedFiltersContextProvider,
	useAdvancedFiltersContext,
} from '../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { Button } from '../../../components-v2/Button';
import {
	AppDrawer,
	AppDrawerContent,
	AppDrawerSeparator,
	AppDrawerToolbar,
	AppDrawerToolbarButton,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../components-v2/Drawer/AppDrawer';
import { Column, Row } from '../../../components-v2/PageLayout';
import { AssigneeIcon } from '../../../components/svgs/icons/AssigneeIcon';
import { TableSettingsIcon } from '../../../components/svgs/icons/TableSettingsIcon';
import { TextSearchIcon } from '../../../components/svgs/icons/TextSearchIcon';
import { TaggingGameLink } from '../../../components/TaggingGameLink';
import { ROUTES } from '../../../constants/routes';
import { bs } from '../../../styles/styles';
import { DefaultUniversalAssistantChat } from '../../templates/AssistantChat';
import { ContactInfoAppDrawerContent } from './AppDrawerContents/ContactInfo';
import { TableSettingsAppDrawerContent } from './AppDrawerContents/TableSettings';
import { CompoundFilters } from './CompoundFilters/CompoundFilters';
import { TableColumnVisibilityProvider } from './context/TableColumnVisibilityContext';
import { useContactSortPreferences } from './hooks/useContactSortPreferences';
import {
	PeopleSearchStorageKey,
	useContactsPageAppDrawerQueryParamState,
} from './hooks/useContactsPageAppDrawerQueryParamState';
import { useIsDuplicateGroupingToggled } from './hooks/useIsDuplicateGroupingToggled';
import { PeopleActionsBar } from './PeopleActionsBar';
import {
	CONTACT_SORT_PREFERENCE_SUFFIX,
	DefaultPeopleFilter,
	DefaultPeopleSort,
	getSearchFromCriteria,
	getSelectedSearchCriterionProperty,
	PeopleSearchContext,
} from './PeopleContext';
import { PeopleGlobalSearch } from './PeopleGlobalSearch';
import { PeopleSearchDescription } from './PeopleSearchDescription';
import { PeopleTable } from './PeopleTable';
import { SaveSearch } from './SaveSearch';
import { styleSheet } from './styles';
import { getPeopleSearchPathUpdate } from './utils/peopleSearchUrl';

interface IProps extends IQuickAddEntityComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	onContactsLoad?(filter: Api.IBulkContactsRequest, contacts: Api.IContact[]): void;
}

const _PeopleContainer: React.FC<IProps> = inject(QuickAddEntityViewModelKey)(
	observer(props => {
		const { quickAddEntity, onContactsLoad } = props;
		const history = useHistory();
		const location = useLocation();

		const userSession = useUserSession();

		// Get search from query string or storage
		const params = getQueryStringParams(location?.search);
		const currentSearchParam = params?.search;
		const currentSortParam = params?.sort;
		const drawerState = useContactsPageAppDrawerQueryParamState();
		const storedContactsQueryStringValue = PersistentStorageManager.local.get(PeopleSearchStorageKey);
		const storedContactsQuery: {
			searchRequest: Api.IBulkContactsRequest;
			sortDescriptor: Api.ISortDescriptor;
		} = storedContactsQueryStringValue
			? (() => {
					try {
						return JSON.parse(storedContactsQueryStringValue);
					} catch {
						// Clear corrupted stored query
						PersistentStorageManager.local.remove(PeopleSearchStorageKey);
						return null;
					}
				})()
			: null;
		const contactsQueryParams = queryStringParamsToSearchRequest<Api.IBulkContactsRequest>(params);
		let queryFilter = { filter: DefaultPeopleFilter.filter } as Api.IBulkContactsRequest;
		if (contactsQueryParams?.searchRequest?.filter?.criteria) {
			queryFilter = contactsQueryParams.searchRequest;
		} else if (storedContactsQuery?.searchRequest?.filter?.criteria) {
			queryFilter = storedContactsQuery.searchRequest;
		}

		// Calculate initial sort with user preferences
		const initialUserPreference = userSession.user?.id
			? (() => {
					try {
						const stored = PersistentStorageManager.local.get(
							`${userSession.user.id}${CONTACT_SORT_PREFERENCE_SUFFIX}`
						);
						return stored ? JSON.parse(stored) : null;
					} catch {
						return null;
					}
				})()
			: null;
		const initialSort =
			contactsQueryParams?.sortDescriptor ||
			storedContactsQuery?.sortDescriptor ||
			initialUserPreference ||
			DefaultPeopleSort;

		const [sort, setSort] = React.useState<Api.ISortDescriptor>(initialSort);

		// Use the contact sort preferences hook for ongoing functionality
		const { isNameSort, loadUserSortPreference } = useContactSortPreferences({
			userSession,
			currentSort: sort,
			contactsQueryParams,
			storedContactsQuery,
		});

		// Filter Context
		const initialBulkContactsFilter: Api.IBulkContactsRequest = {
			...queryFilter,
			includeContactIds: [], // Used for narrowing down results to duplicate contacts of a specific contact
		};
		const [filter, setFilter] = React.useState<Api.IBulkContactsRequest>(initialBulkContactsFilter);
		const isDuplicateGroupingToggled = useIsDuplicateGroupingToggled({ request: filter });

		// Selection state
		const [selectionState, setSelectionState] = React.useState<ESelectionState>(ESelectionState.None);
		const [selectedContactIds, setSelectedContactIds] = React.useState<string[]>([]);
		const [excludedContactIds, setExcludedContactIds] = React.useState<string[]>([]);

		// React Query for contacts
		const contactsQuery = useContactFilterQuery({
			filterRequest: { ...filter, groupByDuplicate: isDuplicateGroupingToggled },
			sortBy: sort.sortBy as ContactSortKey,
			sort: sort.sort as 'asc' | 'desc',
			expand: `TagsCollection${isDuplicateGroupingToggled ? ',DuplicateContacts' : ''}`,
			enabled: true,
		});

		// Extract contacts from query results
		const contacts = React.useMemo(
			() => (contactsQuery.data?.pages?.flatMap(page => page.values) ?? []) as Api.IContact[],
			[contactsQuery.data]
		);

		// Memoize the context value
		const peopleSearchValue = React.useMemo(
			() => ({
				contacts,
				filter,
				setFilter,
				sort,
				setSort,
				contactsQuery,
				selectionState,
				setSelectionState,
				selectedContactIds,
				setSelectedContactIds,
				excludedContactIds,
				setExcludedContactIds,
			}),
			[contacts, filter, sort, contactsQuery, selectionState, selectedContactIds, excludedContactIds]
		);

		const load = React.useCallback(
			({
				newFilter = filter,
				newSort = sort,
				replaceUrl = false,
			}: {
				newFilter?: Api.IBulkContactsRequest;
				newSort?: Api.ISortDescriptor;
				replaceUrl?: boolean;
			} = {}) => {
				setFilter({
					...newFilter,
					includeContactIds: [],
				});
				setSort(newSort);

				// Reset selection state on filter/sort change
				setSelectionState(Api.ESelectionState.None);
				setSelectedContactIds([]);
				setExcludedContactIds([]);

				const notEmptyCriteria = newFilter.filter.criteria.filter(x => x.criteria?.length || x.property != null);
				const searchFilter: Api.IBulkContactsRequest = {
					...newFilter,
					filter: {
						...newFilter.filter,
						criteria: notEmptyCriteria,
					},
				};
				const nextPath = getPeopleSearchPathUpdate(searchFilter, newSort, {
					search: currentSearchParam,
					sort: currentSortParam,
				});
				if (nextPath) {
					if (replaceUrl) {
						history.replace(nextPath);
					} else {
						history.push(nextPath);
					}
				}
			},
			[currentSearchParam, currentSortParam, filter, history, sort]
		);

		// Search Input Context
		const [search, setSearch] = React.useState(() => getSearchFromCriteria(queryFilter.filter?.criteria));
		const activeSearchCriterionProperty = getSelectedSearchCriterionProperty(queryFilter.filter?.criteria);

		// Normalizes the current entry rather than navigating, so it must replace: pushing here truncates the forward
		// stack every time the list is reached by pressing back.
		React.useEffect(() => {
			load({ newFilter: filter, replaceUrl: true });
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		React.useEffect(() => {
			setFilter(initialBulkContactsFilter);
			setSort(initialSort);
			setSearch(getSearchFromCriteria(queryFilter.filter?.criteria));
			if (!location?.pathname.includes('companies')) {
				PersistentStorageManager.local.setObject(PeopleSearchStorageKey, contactsQueryParams);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [location]);

		// Notify parent when contacts load
		React.useEffect(() => {
			if (contacts.length > 0 && onContactsLoad) {
				onContactsLoad(filter, contacts);
			}
		}, [contacts, filter, onContactsLoad]);

		const onShowQuickAddContactModal = () => {
			quickAddEntity.show({
				entity: {},
				onComplete: (error: Api.IOperationResultNoValue, contactModel?: Api.IContact) => {
					if (!error && !!contactModel) {
						invalidateAllContactFilterQueries();
						quickAddEntity.reset();
						const locationState: ILocationState<Api.ContactViewModel, Api.IContact> = {
							viewModel: new Api.ContactViewModel(userSession, contactModel),
						};
						const contactLink = {
							pathname: `${ROUTES.contacts.people}/${contactModel.id}`,
							state: locationState,
						};
						history.push(contactLink);
					}
				},
				type: 'contact',
			});
		};

		const onShowContactsSelfImport = () => history.push(`${ROUTES.contacts.people}/import`);

		const handleFilterChange = (newFilter: Api.IBulkContactsRequest) => {
			load({ newFilter });
		};

		const handleCompoundFilterChange = (
			filterCriteria: Api.IContactFilterCriteria[],
			_householdGrouping?: boolean,
			ownershipFilter?: Api.IOwnershipFilter
		) => {
			const nextFilter = produce(filter, draftFilter => {
				draftFilter.filter.criteria = filterCriteria;
				draftFilter.ownershipFilter = ownershipFilter;
			});
			load({ newFilter: nextFilter });
		};

		const handleClearSearch = React.useCallback(() => {
			setSearch('');
			// Preserve name sorting but reset creation date sorting to default
			// Also fallback to latest user preference if current sort is not a name sort
			const sortToUse = isNameSort(sort) ? sort : loadUserSortPreference() || DefaultPeopleSort;
			load({ newFilter: DefaultPeopleFilter, newSort: sortToUse });
		}, [load, sort, loadUserSortPreference, isNameSort]);

		const handleClearSearchWithContext = React.useCallback(() => {
			setSearch('');
			const sortToUse = isNameSort(sort) ? sort : loadUserSortPreference() || DefaultPeopleSort;
			load({ newFilter: DefaultPeopleFilter, newSort: sortToUse });
		}, [load, sort, loadUserSortPreference, isNameSort]);

		// Only show "Clear search" if filter differs from default OR sort differs from default AND it's not a name sort
		// (name sorts are persistent user preferences, so they don't count as "searched")
		const hasSearched = !equal(DefaultPeopleFilter, filter) || (!equal(DefaultPeopleSort, sort) && !isNameSort(sort));

		return (
			<PeopleSearchContext.Provider value={peopleSearchValue}>
				<AdvancedFiltersContextProvider
					initialFilterCriteria={initialBulkContactsFilter.filter.criteria}
					initialOwnershipFilter={filter.ownershipFilter}
					onSave={handleCompoundFilterChange}
					enforceSystemExcludedTags={false}
				>
					<TableColumnVisibilityProvider>
						<Column styles={[styleSheet.container]}>
							<Row styles={[styleSheet.searchRow]}>
								<Row styles={[styleSheet.searchControls]}>
									<TaggingGameLink />
									<PeopleGlobalSearch
										search={search}
										onSearchChange={setSearch}
										activeSearchCriterionProperty={activeSearchCriterionProperty}
										filter={filter}
										onFilterChange={handleFilterChange}
									/>
									{hasSearched && <ClearGlobalSearchButton onClearSearch={handleClearSearch} />}
								</Row>
								<Row styles={[styleSheet.addContactsControls]}>
									<Button onClick={onShowQuickAddContactModal} styles={[styleSheet.addContactButtons]}>
										New Contact
									</Button>
									<Button onClick={onShowContactsSelfImport} styles={[styleSheet.addContactButtons]}>
										Import from File
									</Button>
								</Row>
							</Row>

							<Row styles={[styleSheet.searchDescription]}>
								<PeopleSearchDescription filter={filter} onChangeFilter={handleFilterChange} />
								<SaveSearch filter={filter} />
							</Row>
							<Row>
								<PeopleActionsBar sort={sort} filter={filter} onReload={load} onChangeFilter={handleFilterChange} />
							</Row>
							<Column styles={[bs.flex1]}>
								<PeopleTableWithContextReset onBasicClearFilters={handleClearSearchWithContext} load={load} />
							</Column>
							<AppDrawer
								value={drawerState.activeDrawer}
								onValueChange={value => {
									if (!value) {
										drawerState.clearDrawerValue();
										return;
									}
									if (value === 'contact-info') {
										drawerState.setDrawerToContactInfo(drawerState.drawerContactId ?? contacts?.[0]?.id);
										return;
									}
									drawerState.setDrawerValue(value);
								}}
							>
								<ContactInfoAppDrawerContent contactId={drawerState.drawerContactId} />
								<AppDrawerContent value='filter'>
									<CompoundFilters load={load} onClear={handleClearSearch} />
								</AppDrawerContent>
								<TableSettingsAppDrawerContent />
								<StickyAppDrawerContent value='ai-assistant'>
									<DefaultUniversalAssistantChat />
								</StickyAppDrawerContent>
								<AppDrawerToolbar>
									<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
									<AppDrawerSeparator />
									<AppDrawerToolbarButton value='filter' tooltip='Filter contacts'>
										<TextSearchIcon />
									</AppDrawerToolbarButton>
									<AppDrawerToolbarButton value='contact-info' tooltip='Contact preview'>
										<AssigneeIcon />
									</AppDrawerToolbarButton>
									<AppDrawerToolbarButton value='table-settings' tooltip='Table settings'>
										<TableSettingsIcon />
									</AppDrawerToolbarButton>
								</AppDrawerToolbar>
							</AppDrawer>
						</Column>
					</TableColumnVisibilityProvider>
				</AdvancedFiltersContextProvider>
			</PeopleSearchContext.Provider>
		);
	})
);

export const PeopleContainer: React.FC<IProps> = props => <PeopleContainerInner {...props} />;

const PeopleContainerInner = _PeopleContainer;

const PeopleTableWithContextReset = ({
	onBasicClearFilters,
	load,
}: {
	onBasicClearFilters: () => void;
	load: ({ newFilter, newSort }?: { newFilter?: Api.IBulkContactsRequest; newSort?: Api.ISortDescriptor }) => void;
}) => {
	const {
		createDateFilters,
		lastCommunicationDateFilters,
		tagFilters,
		keyFactFilters,
		customFieldFilters,
		locationFilters,
		companyTagFilters,
	} = useAdvancedFiltersContext();

	const handleClearFiltersWithContext = React.useCallback(() => {
		createDateFilters.refreshDateFilter(DefaultPeopleFilter?.filter?.criteria);
		lastCommunicationDateFilters.refreshDateFilter(DefaultPeopleFilter?.filter?.criteria);
		tagFilters.refreshTags(DefaultPeopleFilter?.filter?.criteria);
		keyFactFilters.refreshKeyFacts(DefaultPeopleFilter?.filter?.criteria);
		customFieldFilters.refreshCustomFieldFilters(DefaultPeopleFilter?.filter?.criteria);
		locationFilters.refreshLocationFilters(DefaultPeopleFilter?.filter?.criteria);
		companyTagFilters.refreshCompanyTagFilters(DefaultPeopleFilter?.filter?.criteria);
		onBasicClearFilters();
	}, [
		createDateFilters,
		lastCommunicationDateFilters,
		tagFilters,
		keyFactFilters,
		customFieldFilters,
		locationFilters,
		companyTagFilters,
		onBasicClearFilters,
	]);

	return <PeopleTable onClearFilters={handleClearFiltersWithContext} onSortChanged={newSort => load({ newSort })} />;
};

const ClearGlobalSearchButton = ({ onClearSearch }: { onClearSearch: () => void }) => {
	const {
		createDateFilters,
		lastCommunicationDateFilters,
		tagFilters,
		keyFactFilters,
		customFieldFilters,
		locationFilters,
		companyTagFilters,
	} = useAdvancedFiltersContext();
	const handleClear = () => {
		createDateFilters.refreshDateFilter(DefaultPeopleFilter?.filter?.criteria);
		lastCommunicationDateFilters.refreshDateFilter(DefaultPeopleFilter?.filter?.criteria);
		tagFilters.refreshTags(DefaultPeopleFilter?.filter?.criteria);
		keyFactFilters.refreshKeyFacts(DefaultPeopleFilter?.filter?.criteria);
		customFieldFilters.refreshCustomFieldFilters(DefaultPeopleFilter?.filter?.criteria);
		locationFilters.refreshLocationFilters(DefaultPeopleFilter?.filter?.criteria);
		companyTagFilters.refreshCompanyTagFilters(DefaultPeopleFilter?.filter?.criteria);
		onClearSearch();
	};
	return (
		<Button variant='secondary' onClick={handleClear} styles={[styleSheet.clearSearchButton]}>
			Clear search
		</Button>
	);
};
